/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';
import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest
} from 'redux-saga/effects';

import * as api from '@ibm-watson/shared/modules/dialog/api/nodes';
import * as apiWorkspaces from '@ibm-watson/shared/modules/dialog/api/workspaces';
import { isCacheExpired } from '@ibm-watson/shared/modules/ia/store/helper';
import * as messagesActions from '@ibm-watson/shared/modules/ia/store/messages/actions';
import { getErrorString } from '@ibm-watson/shared/modules/ia/util/errorHandler';
import { ReactivateSession } from '../../../../ts/session';
import { selectIsAccessUpdatesEnabled } from '../../../ia/store/features/selectors';
import { selectIsNewIa } from '../../../ia/store/instance/selectors';
import { getAssistantId } from '../../../ia/store/session/selectors';
import { getCallout } from '../../util/dialog/DialogTreeUtil';
import * as actions from './actions';
import * as actionType from './actionTypes';
import { getFirstResponseByConditions } from './selectors';

const { addMessage } = messagesActions;

const generateNodeId = node => {
  let idPrefix = 'node_';
  if (node.type === 'response_condition') {
    idPrefix = 'response_';
  } else if (node.type === 'slot') {
    idPrefix = 'slot_';
  } else if (node.type === 'event_handler') {
    idPrefix = 'handler_';
  }
  return (
    idPrefix + Math.floor(Math.random() * 10 + 1) + '_' + new Date().getTime()
  );
};

const getError = e => {
  let error = e;
  if (e.response && e.response.data) {
    error = e.response.data.error;
    if (e.response.data.errors && e.response.data.errors.length > 0) {
      error = `${error} ${e.response.data.errors[0].path} :
      ${e.response.data.errors[0].message}`;
    }
  } else if (e.message) {
    error = e.message;
  } else {
    error = e.toString();
  }
  return error;
};

function* refreshNodes(action) {
  // TODO when accessUpdates enabled everywhere we pass just isNewIA
  const assistantId = yield select(getAssistantId);
  const isNewIA = yield select(selectIsNewIa);
  const accessUpdatesEnabled = yield select(selectIsAccessUpdatesEnabled);
  const freshData = yield call(
    api.listNodes,
    action.skillId,
    isNewIA && accessUpdatesEnabled,
    assistantId,
    {
      include_audit: true
    }
  );
  yield put(actions.clearNodes(true));
  yield put(actions.addNodes(freshData));
}

function* watchRefreshNodes() {
  yield takeEvery(actionType.REFRESH_NODES, refreshNodes);
}

function* fetchNodesIfRequired(action) {
  const state = yield select();
  const lastFetched = state?.dialog?.lastFetched;
  if (!lastFetched || isCacheExpired(lastFetched)) {
    yield fetchNodes(action, ReactivateSession.NEVER);
  } else {
    yield put(actions.addNodesCache());
  }
}
// FETCH NODES
export function* fetchNodes(action, reactivateSession) {
  try {
    const assistantId = yield select(getAssistantId);
    const isNewIA = yield select(selectIsNewIa);
    const accessUpdatesEnabled = yield select(selectIsAccessUpdatesEnabled);

    yield put(actions.clearNodes(action.selectedNode));

    // TODO when accessUpdates enabled everywhere we pass just isNewIA
    const nodes = yield call(
      api.listNodes,
      action.skillId,
      isNewIA && accessUpdatesEnabled,
      assistantId,
      {
        include_audit: true
      },
      reactivateSession
    );
    yield put(actions.addNodes(nodes));
  } catch (e) {
    console.log(e); // eslint-disable-line
    yield put(
      addMessage({
        status: 'error',
        message: getError(e),
        transactionId: e.transactionId
      })
    );
  }
}

function* watchFetchNodesIfRequired() {
  yield takeLatest(actionType.FETCH_NODES_IF_REQUIRED, fetchNodesIfRequired);
}

function* watchFetchNodes() {
  yield takeLatest(actionType.FETCH_NODES, fetchNodes);
}

// ADD NODE
export function* addNode(action) {
  try {
    if (!action.newNode.dialog_node) {
      action.newNode.dialog_node = generateNodeId(action.newNode);
    }
    yield put(actions.addNodeStart(action.newNode, action.select));
    const data = yield call(
      api.createNode,
      action.skillId,
      action.useNewIAUpdatedAccess,
      action.assistantId,
      action.newNode,
      {
        include_audit: true
      }
    );
    yield put(actions.addNodeSuccess(data, action.select));
  } catch (e) {
    console.log(e); // eslint-disable-line
    yield put(actions.addNodeError(action.newNode, action.select));
    yield put(actions.refreshNodes(action.skillId));
    yield put(
      addMessage({
        status: 'error',
        message: getErrorString(e),
        transactionId: e.transactionId
      })
    );
  }
}

function* watchAddNode() {
  yield takeEvery(actionType.ADD_NODE, addNode);
}

// DELETE NODE
export function* deleteNode(action) {
  const { node, skillId, childNodesToPreserve } = action;
  let childNodes = [];
  try {
    const assistantId = yield select(getAssistantId);
    const isNewIA = yield select(selectIsNewIa);
    const accessUpdatesEnabled = yield select(selectIsAccessUpdatesEnabled);

    yield put(actions.deleteNodeStart(node));

    if (childNodesToPreserve && childNodesToPreserve.length > 0) {
      let prevNode = node;
      childNodes = childNodesToPreserve
        .filter(childNode => childNode.parent === node.dialog_node)
        .map(childNode => {
          const newChildNode = {
            dialog_node: childNode.dialog_node,
            parent: node.parent || null,
            previous_sibling: prevNode.dialog_node || null
          };

          prevNode = childNode;
          return newChildNode;
        });

      // TODO when accessUpdates enabled everywhere we pass just isNewIA
      for (let i = 0; i < childNodes.length; i++) {
        yield call(
          api.updateNode,
          skillId,
          isNewIA && accessUpdatesEnabled,
          assistantId,
          childNodes[i].dialog_node,
          childNodes[i]
        );
      }
    }

    // TODO when accessUpdates enabled everywhere we pass just isNewIA
    yield call(
      api.deleteNode,
      skillId,
      isNewIA && accessUpdatesEnabled,
      assistantId,
      node.dialog_node
    );

    yield put(actions.deleteNodeSuccess(node, childNodesToPreserve));
  } catch (e) {
    console.log(e); // eslint-disable-line
    yield put(actions.deleteNodeError(node));
    yield put(
      addMessage({
        status: 'error',
        message: getError(e),
        transactionId: e.transactionId
      })
    );
  }
}

function* watchDeleteNode() {
  yield takeEvery(actionType.DELETE_NODE, deleteNode);
}

// UPDATE NODE
export function* updateNode(action) {
  try {
    yield put(
      actions.updateNodeStart(
        action.nodeId,
        action.node,
        action.useNewIAUpdatedAccess,
        action.assistantId
      )
    );
    const updatedNode = yield call(
      api.updateNode,
      action.skillId,
      action.useNewIAUpdatedAccess,
      action.assistantId,
      action.nodeId,
      action.node
    );
    yield put(actions.updateNodeSuccess(action.nodeId, updatedNode));
  } catch (e) {
    console.log(e); // eslint-disable-line
    yield put(actions.updateNodeError(action.nodeId, action.node));
    yield put(actions.refreshNodes(action.skillId));
    yield put(
      addMessage({
        status: 'error',
        message: getError(e),
        transactionId: e.transactionId
      })
    );
  }
}

function* watchUpdateNode() {
  yield takeEvery(actionType.UPDATE_NODE, updateNode);
}

// UPDATE SLOT
export function* updateSlot(action) {
  try {
    const assistantId = yield select(getAssistantId);
    const isNewIA = yield select(selectIsNewIa);
    const accessUpdatesEnabled = yield select(selectIsAccessUpdatesEnabled);
    const useNewIAUpdatedAccess = isNewIA && accessUpdatesEnabled;

    yield put(
      actions.updateSlotStart(
        action.slot,
        action.inputHandler,
        action.focusHandler
      )
    );
    yield call(
      api.updateNode,
      action.skillId,
      useNewIAUpdatedAccess,
      assistantId,
      action.slot.dialog_node,
      action.slot
    );
    yield call(
      api.updateNode,
      action.skillId,
      useNewIAUpdatedAccess,
      assistantId,
      action.inputHandler.dialog_node,
      action.inputHandler
    );
    yield call(
      api.updateNode,
      action.skillId,
      useNewIAUpdatedAccess,
      assistantId,
      action.focusHandler.dialog_node,
      action.focusHandler
    );
    yield put(
      actions.updateSlotSuccess(
        action.slot,
        action.inputHandler,
        action.focusHandler
      )
    );
  } catch (e) {
    console.log(e); // eslint-disable-line
    yield put(
      actions.updateSlotError(
        action.slot,
        action.inputHandler,
        action.focusHandler
      )
    );
    yield put(actions.refreshNodes(action.skillId));
    yield put(
      addMessage({
        status: 'error',
        message: getError(e),
        transactionId: e.transactionId
      })
    );
  }
}

// REPLACE SLOT AND HANDLERS
export function* updateSlotAndHandlers(action) {
  const { skillId, slot, inputHandler, focusHandler, otherHandlers } = action;
  const slotAndHandlers = [slot, inputHandler, focusHandler, ...otherHandlers];
  const newNodeIdMap = {};

  const assistantId = yield select(getAssistantId);
  const isNewIA = yield select(selectIsNewIa);
  const accessUpdatesEnabled = yield select(selectIsAccessUpdatesEnabled);

  slotAndHandlers.forEach(node => {
    if (node.dialog_node) {
      newNodeIdMap[node.dialog_node] = node.dialog_node + '_copy';
    }
  });

  const slotAndHandlersWithNewIds = slotAndHandlers.map(node => {
    return {
      ...node,
      dialog_node:
        node.dialog_node in newNodeIdMap
          ? newNodeIdMap[node.dialog_node]
          : node.dialog_node,
      previous_sibling:
        node.previous_sibling in newNodeIdMap
          ? newNodeIdMap[node.previous_sibling]
          : node.previous_sibling,
      parent:
        node.parent in newNodeIdMap ? newNodeIdMap[node.parent] : node.parent
    };
  });

  slotAndHandlersWithNewIds[0].previous_sibling = slot.dialog_node;

  try {
    yield put(actions.updateSlotStart(slot, inputHandler, focusHandler));

    try {
      // TODO when accessUpdates enabled everywhere we pass just isNewIA
      yield call(
        apiWorkspaces.updateWorkspace,
        skillId,
        isNewIA && accessUpdatesEnabled,
        assistantId,
        { dialog_nodes: slotAndHandlersWithNewIds },
        { append: true }
      );
    } catch (e) {
      yield put(
        addMessage({
          status: 'error',
          message: getError(e),
          transactionId: e.transactionId
        })
      );
      yield put(actions.updateSlotAndHandlersError(slotAndHandlers));

      return;
    }

    // TODO when accessUpdates enabled everywhere we pass just isNewIA
    yield call(
      api.deleteNode,
      skillId,
      isNewIA && accessUpdatesEnabled,
      assistantId,
      slot.dialog_node
    );

    // TODO when accessUpdates enabled everywhere we pass just isNewIA
    yield call(
      apiWorkspaces.updateWorkspace,
      skillId,
      isNewIA && accessUpdatesEnabled,
      assistantId,
      { dialog_nodes: slotAndHandlers },
      { append: true }
    );
    // TODO when accessUpdates enabled everywhere we pass just isNewIA
    yield call(
      api.deleteNode,
      skillId,
      isNewIA && accessUpdatesEnabled,
      assistantId,
      newNodeIdMap[slot.dialog_node]
    );
    yield put(actions.updateSlotAndHandlersSuccess(slotAndHandlers));
  } catch (e) {
    yield put(actions.updateSlotAndHandlersError(slotAndHandlers));
    yield put(actions.refreshNodes(skillId));
    yield put(
      addMessage({
        status: 'error',
        message: getError(e),
        transactionId: e.transactionId
      })
    );
  }
}

function* watchUpdateSlot() {
  yield takeEvery(actionType.UPDATE_SLOT, updateSlot);
}

// UPDATE HANDLERS
export function* updateHandlers(action) {
  const assistantId = yield select(getAssistantId);
  const isNewIA = yield select(selectIsNewIa);
  const accessUpdatesEnabled = yield select(selectIsAccessUpdatesEnabled);
  const useNewIAUpdatedAccess = isNewIA && accessUpdatesEnabled;
  try {
    yield put(
      actions.updateHandlersStart(
        action.nodeId,
        action.handlerType,
        action.existingHandlers,
        action.handlers
      )
    );
    for (let i = 0; i < action.existingHandlers.length; i++) {
      // Does handler still exist?  If not, delete it
      const existing = _.findIndex(action.handlers, {
        dialog_node: action.existingHandlers[i].dialog_node
      });
      if (existing < 0) {
        yield call(
          api.deleteNode,
          action.skillId,
          useNewIAUpdatedAccess,
          assistantId,
          action.existingHandlers[i].dialog_node
        );
      }
    }
    for (let i = 0; i < action.handlers.length; i++) {
      // Does handler exist?  If not, update it, otherwise create it
      const existing = _.findIndex(action.existingHandlers, {
        dialog_node: action.handlers[i].dialog_node
      });
      if (existing < 0) {
        if (!action.handlers[i].dialog_node) {
          action.handlers[i].dialog_node = generateNodeId(action.handlers[i]);
        }
        action.handlers[i].previous_sibling = getPreviousSibling(
          action.handlers,
          i
        );
        yield call(
          api.createNode,
          action.skillId,
          useNewIAUpdatedAccess,
          assistantId,
          action.handlers[i],
          {}
        );
      } else {
        action.handlers[i].previous_sibling = getPreviousSibling(
          action.handlers,
          i
        );
        yield call(
          api.updateNode,
          action.skillId,
          useNewIAUpdatedAccess,
          assistantId,
          action.handlers[i].dialog_node,
          action.handlers[i]
        );
      }
    }
    yield put(
      actions.updateHandlersSuccess(
        action.nodeId,
        action.handlerType,
        action.existingHandlers,
        action.handlers
      )
    );
    yield put(actions.refreshNodes(action.skillId));
  } catch (e) {
    console.log(e); // eslint-disable-line
    yield put(
      actions.updateHandlersError(
        action.nodeId,
        action.handlerType,
        action.existingHandlers,
        action.handlers
      )
    );
    yield put(actions.refreshNodes(action.skillId));
    yield put(
      addMessage({
        status: 'error',
        message: getError(e),
        transactionId: e.transactionId
      })
    );
  }
}

function getPreviousSibling(handlers, index) {
  if (index > 0) {
    return handlers[index - 1].dialog_node;
  } else {
    return null;
  }
}

function* watchUpdateHandlers() {
  yield takeEvery(actionType.UPDATE_HANDLERS, updateHandlers);
}

function* watchUpdateSlotAndHandlers() {
  yield takeEvery(actionType.UPDATE_SLOT_AND_HANDLERS, updateSlotAndHandlers);
}

// ADD SLOT
export function* addSlot(action) {
  try {
    const assistantId = yield select(getAssistantId);
    const isNewIA = yield select(selectIsNewIa);
    const accessUpdatesEnabled = yield select(selectIsAccessUpdatesEnabled);
    const useNewIAUpdatedAccess = isNewIA && accessUpdatesEnabled;

    if (!action.newSlot.dialog_node) {
      action.newSlot.dialog_node = generateNodeId(action.newSlot);
    }
    yield put(actions.addSlotStart(action.newSlot));

    // Create input and focus handler for slot
    const inputHandler = {
      type: 'event_handler',
      event_name: 'input',
      parent: action.newSlot.dialog_node,
      previous_sibling: null
    };
    inputHandler.dialog_node = generateNodeId(inputHandler);
    const focusHandler = {
      type: 'event_handler',
      event_name: 'focus',
      parent: action.newSlot.dialog_node,
      previous_sibling: inputHandler.dialog_node,
      output: {}
    };
    focusHandler.dialog_node = generateNodeId(focusHandler);
    // Ensure handlers are not given same ID
    while (focusHandler.dialog_node === inputHandler.dialog_node) {
      focusHandler.dialog_node = generateNodeId(focusHandler);
    }
    // Save slot and handlers
    const dataSlot = yield call(
      api.createNode,
      action.skillId,
      useNewIAUpdatedAccess,
      assistantId,
      action.newSlot,
      {}
    );
    const dataInputHandler = yield call(
      api.createNode,
      action.skillId,
      useNewIAUpdatedAccess,
      assistantId,
      inputHandler,
      {}
    );
    const dataFocusHandler = yield call(
      api.createNode,
      action.skillId,
      useNewIAUpdatedAccess,
      assistantId,
      focusHandler,
      {}
    );
    yield put(
      actions.addSlotSuccess(dataSlot, dataInputHandler, dataFocusHandler)
    );
  } catch (e) {
    console.log(e); // eslint-disable-line
    yield put(actions.addSlotError(action.newSlot));
    yield put(actions.refreshNodes(action.skillId));
    yield put(
      addMessage({
        status: 'error',
        message: getError(e),
        transactionId: e.transactionId
      })
    );
  }
}

function* watchAddSlot() {
  yield takeEvery(actionType.ADD_SLOT, addSlot);
}

// ADD RESPONSE
export function* addResponse(action) {
  try {
    const assistantId = yield select(getAssistantId);
    const isNewIA = yield select(selectIsNewIa);
    const accessUpdatesEnabled = yield select(selectIsAccessUpdatesEnabled);
    const useNewIAUpdatedAccess = isNewIA && accessUpdatesEnabled;

    if (!action.newResponse.dialog_node) {
      action.newResponse.dialog_node = generateNodeId(action.newResponse);
    }
    yield put(actions.addResponseStart(action.newResponse));

    const data = yield call(
      api.createNode,
      action.skillId,
      useNewIAUpdatedAccess,
      assistantId,
      action.newResponse,
      {}
    );
    yield put(actions.addResponseSuccess(data));
  } catch (e) {
    console.log(e); // eslint-disable-line
    yield put(actions.addResponseError(action.newResponse));
    yield put(
      addMessage({
        status: 'error',
        message: getError(e),
        transactionId: e.transactionId
      })
    );
  }
}

function* watchAddResponse() {
  yield takeEvery(actionType.ADD_RESPONSE, addResponse);
}

// ENABLE MCR
export function* enableMcr(action) {
  try {
    let updatedNode = {};
    const assistantId = yield select(getAssistantId);
    const isNewIA = yield select(selectIsNewIa);
    const accessUpdatesEnabled = yield select(selectIsAccessUpdatesEnabled);
    const useNewIAUpdatedAccess = isNewIA && accessUpdatesEnabled;

    yield put(actions.enableMcrStart(action.node, action.enable));

    if (action.enable) {
      if (action.responses) {
        // Existing responses so set metadata flag and enable response nodes
        for (let i = 0; i < action.responses.length; i++) {
          action.responses[i].disabled = null;
          if (i === 0) {
            action.responses[i].output = action.node.output;
            action.responses[i].context = action.node.context;
            action.responses[i].actions = action.node.actions;
          }
          yield call(
            api.updateNode,
            action.skillId,
            useNewIAUpdatedAccess,
            assistantId,
            action.responses[i].dialog_node,
            action.responses[i]
          );
        }

        updatedNode.output = null;
        updatedNode.context = null;
        updatedNode.actions = null;
        updatedNode.metadata = action.node.metadata;
        _.set(updatedNode, 'metadata._customization.mcr', true);
        yield call(
          api.updateNode,
          action.skillId,
          useNewIAUpdatedAccess,
          assistantId,
          action.node.dialog_node,
          updatedNode
        );
      } else {
        // No existing responses, so create first one and transfer
        // existing output, context and actions to response
        const firstResponse = {
          type: 'response_condition',
          parent: action.node.dialog_node,
          output: action.node.output,
          context: action.node.context,
          actions: action.node.actions
        };
        firstResponse.dialog_node = generateNodeId(firstResponse);

        const firstResponseNode = yield call(
          api.createNode,
          action.skillId,
          useNewIAUpdatedAccess,
          assistantId,
          firstResponse,
          {}
        );
        action.responses = [firstResponseNode];

        updatedNode = {
          output: null,
          context: null,
          actions: null,
          metadata: action.node.metadata
        };
        _.set(updatedNode, 'metadata._customization.mcr', true);
        yield call(
          api.updateNode,
          action.skillId,
          useNewIAUpdatedAccess,
          assistantId,
          action.node.dialog_node,
          updatedNode
        );
      }
    } else {
      // Disable response nodes and switch metadata flag
      if (action.responses) {
        for (let i = 0; i < action.responses.length; i++) {
          action.responses[i].disabled = true;
          yield call(
            api.updateNode,
            action.skillId,
            useNewIAUpdatedAccess,
            assistantId,
            action.responses[i].dialog_node,
            action.responses[i]
          );
        }

        if (action.responses.length > 0) {
          const response = action.responses[0];
          updatedNode.output = response.output;
          updatedNode.context = response.context;
          updatedNode.actions = response.actions;
          updatedNode.metadata = action.node.metadata;
        }
      }
      _.set(updatedNode, 'metadata._customization.mcr', false);
      yield call(
        api.updateNode,
        action.skillId,
        useNewIAUpdatedAccess,
        assistantId,
        action.node.dialog_node,
        updatedNode
      );
    }

    updatedNode.dialog_node = action.node.dialog_node;
    yield put(
      actions.enableMcrSuccess(updatedNode, action.responses, action.enable)
    );
  } catch (e) {
    console.log(e); // eslint-disable-line
    yield put(actions.enableMcrError(action.node));
    yield put(actions.refreshNodes(action.skillId));
    yield put(
      addMessage({
        status: 'error',
        message: getError(e),
        transactionId: e.transactionId
      })
    );
  }
}

function* watchEnableMcr() {
  yield takeEvery(actionType.ENABLE_MCR, enableMcr);
}

export function* enableSlots(action) {
  const {
    skillId,
    node,
    enable,
    enablePrompt,
    existingSlots,
    existingPrompt,
    digressOutSlots
  } = action;
  const originalType = node.type;

  const assistantId = yield select(getAssistantId);
  const isNewIA = yield select(selectIsNewIa);
  const accessUpdatesEnabled = yield select(selectIsAccessUpdatesEnabled);
  const useNewIAUpdatedAccess = isNewIA && accessUpdatesEnabled;

  yield put(actions.enableSlotsStart(node));

  let type;

  if (enable) {
    type = 'frame';
  } else if (node.type === 'frame') {
    type = null;
  }

  const digress_out_slots = enable ? digressOutSlots : null;

  const updatedNode = {
    type,
    digress_out_slots
  };

  try {
    const nodeResponse = yield call(
      api.updateNode,
      skillId,
      useNewIAUpdatedAccess,
      assistantId,
      node.dialog_node,
      updatedNode
    );

    if (enable === true) {
      let promptHandler = existingPrompt;

      if (enablePrompt && !existingPrompt) {
        const newPromptHandler = {
          type: 'event_handler',
          event_name: 'focus',
          parent: node.dialog_node
        };

        yield put(actions.addNodeStart(newPromptHandler, false));
        promptHandler = yield call(
          api.createNode,
          skillId,
          useNewIAUpdatedAccess,
          assistantId,
          newPromptHandler,
          {}
        );
        yield put(actions.addNodeSuccess(promptHandler, false));
      } else if (existingPrompt && existingPrompt.disabled !== !enablePrompt) {
        const updateNodeAction = actions.updateNode(
          skillId,
          useNewIAUpdatedAccess,
          assistantId,
          existingPrompt.dialog_node,
          { disabled: !enablePrompt }
        );
        yield call(updateNode, updateNodeAction);
      }

      if (!existingSlots && type === 'frame' && originalType !== type) {
        const newSlot = {
          type: 'slot',
          parent: node.dialog_node,
          previous_sibling: promptHandler ? promptHandler.dialog_node : null,
          variable: null
        };
        const addSlotAction = actions.addSlot(skillId, newSlot);
        yield call(addSlot, addSlotAction);
      }
    }

    yield put(actions.enableSlotsSuccess(node.dialog_node, nodeResponse));
  } catch (e) {
    yield put(actions.refreshNodes(skillId));
    yield put(
      addMessage({
        status: 'error',
        message: getError(e),
        transactionId: e.transactionId
      })
    );
  }
}

function* watchEnableSlots() {
  yield takeEvery(actionType.ENABLE_SLOTS, enableSlots);
}

export function* enableCallout(action) {
  if (
    action.enableMcr !== null &&
    action.calloutInfo &&
    action.calloutInfo.enableCallout
  ) {
    const enableMcrAction = actions.enableMcr(
      action.skillId,
      action.node,
      action.responses,
      !!action.enableMcr
    );
    yield call(enableMcr, enableMcrAction);
  }
  const assistantId = yield select(getAssistantId);
  const isNewIA = yield select(selectIsNewIa);
  const accessUpdatesEnabled = yield select(selectIsAccessUpdatesEnabled);
  const useNewIAUpdatedAccess = isNewIA && accessUpdatesEnabled;

  try {
    const updatedNode = {};
    let newResponses = null;

    yield put(
      actions.enableCalloutStart(
        action.node,
        updatedNode,
        action.calloutInfo,
        action
      )
    );

    const calloutAction = getCallout(updatedNode.actions);
    if (_.get(action, 'calloutInfo.enableCallout')) {
      const successResponse = {
        type: 'response_condition',
        parent: action.node.dialog_node,
        conditions: '$' + calloutAction.result_variable
      };
      const failureResponse = {
        type: 'response_condition',
        parent: action.node.dialog_node,
        conditions: 'anything_else'
      };
      newResponses = [];

      if (
        !getFirstResponseByConditions(
          action.responses,
          successResponse.conditions
        )
      ) {
        if (
          action.responses &&
          action.responses.length === 1 &&
          !action.responses[0].conditions &&
          !_.get(action.responses[0].output, 'generic[0].values.length')
        ) {
          successResponse.dialog_node = action.responses[0].dialog_node;
          const data = yield call(
            api.updateNode,
            action.skillId,
            useNewIAUpdatedAccess,
            assistantId,
            action.responses[0].dialog_node,
            successResponse
          );
          action.responses[0] = Object.assign(action.responses[0], data);
        } else {
          successResponse.dialog_node = generateNodeId(successResponse);
          if (action.responses && action.responses.length > 0) {
            successResponse.previous_sibling =
              action.responses[action.responses.length - 1].dialog_node;
          }
          const dataSuccess = yield call(
            api.createNode,
            action.skillId,
            useNewIAUpdatedAccess,
            assistantId,
            successResponse,
            {}
          );
          newResponses = newResponses.concat([dataSuccess]);
        }
      }

      if (
        !getFirstResponseByConditions(
          action.responses,
          failureResponse.conditions
        )
      ) {
        failureResponse.dialog_node = generateNodeId(failureResponse);
        if (successResponse.dialog_node) {
          failureResponse.previous_sibling = successResponse.dialog_node;
        } else {
          failureResponse.previous_sibling =
            action.responses && action.responses.length > 0
              ? action.responses[action.responses.length - 1].dialog_node
              : undefined;
        }
        const dataFailure = yield call(
          api.createNode,
          action.skillId,
          useNewIAUpdatedAccess,
          assistantId,
          failureResponse,
          {}
        );
        newResponses = newResponses.concat([dataFailure]);
      }
    }

    action.node = yield call(
      api.updateNode,
      action.skillId,
      useNewIAUpdatedAccess,
      assistantId,
      action.node.dialog_node,
      updatedNode
    );
    updatedNode.dialog_node = action.node.dialog_node;
    yield put(
      actions.enableCalloutSuccess(
        updatedNode,
        action.responses,
        newResponses,
        action
      )
    );
  } catch (e) {
    console.log(e); // eslint-disable-line
    yield put(actions.enableCalloutError(action.node, action.enableCallout));
    yield put(actions.refreshNodes(action.skillId));
    yield put(
      addMessage({
        status: 'error',
        message: getError(e),
        transactionId: e.transactionId
      })
    );
  }

  if (action.enableMcr !== null && !action.calloutInfo.enableCallout) {
    const enableMcrAction = actions.enableMcr(
      action.skillId,
      action.node,
      action.responses,
      !!action.enableMcr
    );
    yield call(enableMcr, enableMcrAction);
  }
}

function* watchCalloutCallout() {
  yield takeEvery(actionType.ENABLE_CALLOUT, enableCallout);
}

// MOVE SUBNODE
export function* moveSubnode(action) {
  try {
    const assistantId = yield select(getAssistantId);
    const isNewIA = yield select(selectIsNewIa);
    const accessUpdatesEnabled = yield select(selectIsAccessUpdatesEnabled);
    const useNewIAUpdatedAccess = isNewIA && accessUpdatesEnabled;
    yield put(actions.moveSubnodeStart(action.subnode, action.direction));

    if (action.subnode.type === 'slot' && !action.subnode.variable) {
      action.updatedSubnode.variable = null;
    }

    yield call(
      api.updateNode,
      action.skillId,
      useNewIAUpdatedAccess,
      assistantId,
      action.subnode.dialog_node,
      action.updatedSubnode
    );

    yield put(actions.moveSubnodeSuccess(action.subnode, action.direction));
  } catch (e) {
    console.log(e); // eslint-disable-line
    yield put(actions.moveSubnodeError(action.subnode, action.direction));
    yield put(actions.refreshNodes(action.skillId));
    yield put(
      addMessage({
        status: 'error',
        message: getError(e),
        transactionId: e.transactionId
      })
    );
  }
}

function* watchMoveSubnode() {
  yield takeEvery(actionType.MOVE_SUBNODE, moveSubnode);
}

// MOVE NODE
export function* moveNode(action) {
  const undoNode = {
    parent: action.sourceNode.parent,
    previous_sibling: action.sourceNode.previous_sibling,
    digress_in: action.sourceNode.digress_in
  };

  try {
    const assistantId = yield select(getAssistantId);
    const isNewIA = yield select(selectIsNewIa);
    const accessUpdatesEnabled = yield select(selectIsAccessUpdatesEnabled);

    // TODO when accessUpdates enabled everywhere we pass just isNewIA
    yield put(
      actions.moveNodeStart(action.sourceNode, action.targetNode, action.where)
    );

    const updatedNode = {
      parent: action.sourceNode.parent || null,
      previous_sibling: action.sourceNode.previous_sibling || null,
      digress_in: action.sourceNode.digress_in
    };

    yield call(
      api.updateNode,
      action.skillId,
      isNewIA && accessUpdatesEnabled,
      assistantId,
      action.sourceNode.dialog_node,
      updatedNode
    );
    yield put(actions.moveNodeSuccess(action.sourceNode.dialog_node));
  } catch (e) {
    console.log(e); // eslint-disable-line
    yield put(actions.moveNodeError(action.sourceNode, undoNode));
    yield put(
      addMessage({
        status: 'error',
        message: getError(e),
        transactionId: e.transactionId
      })
    );
  }
}

function* watchMoveNode() {
  yield takeEvery(actionType.MOVE_NODE, moveNode);
}

// DUPLICATE NODE
export function* duplicateNode(action) {
  const copyNode = _.cloneDeep(action.newNode);
  copyNode.parent = action.sourceNode.parent;

  try {
    const assistantId = yield select(getAssistantId);
    const isNewIA = yield select(selectIsNewIa);
    const accessUpdatesEnabled = yield select(selectIsAccessUpdatesEnabled);

    yield put(actions.addNodeStart(copyNode, false));

    const allDuplicates = [];
    yield put(
      actions.duplicateNodeStart(action.sourceNode, copyNode, allDuplicates)
    );
    // TODO when accessUpdates enabled everywhere we pass just isNewIA
    yield call(
      apiWorkspaces.updateWorkspace,
      action.skillId,
      isNewIA && accessUpdatesEnabled,
      assistantId,
      { dialog_nodes: allDuplicates },
      { append: true }
    );
    yield put(actions.duplicateNodeSuccess(copyNode.dialog_node));
  } catch (e) {
    console.log(e); // eslint-disable-line
    yield put(actions.duplicateNodeError(copyNode));
    yield put(actions.refreshNodes(action.skillId));
    yield put(
      addMessage({
        status: 'error',
        message: getErrorString(e),
        transactionId: e.transactionId
      })
    );
  }
}

export function* updateJumpTo(action) {
  const { skillId, updatedJumpTo, noSave } = action;

  try {
    const assistantId = yield select(getAssistantId);
    const isNewIA = yield select(selectIsNewIa);
    const accessUpdatesEnabled = yield select(selectIsAccessUpdatesEnabled);

    yield put(actions.updateJumpToStart(updatedJumpTo, noSave));

    const currentJumpTo = yield select(state => {
      return state.dialog.jumpToMode;
    });

    if (currentJumpTo.selector) {
      const { sourceNode, targetNode, selector } = currentJumpTo;

      const updatedNode = {
        next_step: {
          behavior: 'jump_to',
          dialog_node: targetNode.dialog_node,
          selector: selector
        }
      };

      // TODO when accessUpdates enabled everywhere we pass just isNewIA
      if (currentJumpTo.noSave !== true) {
        const data = yield call(
          api.updateNode,
          skillId,
          isNewIA && accessUpdatesEnabled,
          assistantId,
          sourceNode.dialog_node,
          updatedNode
        );
        yield put(actions.updateJumpToSuccess(data));
      } else {
        yield put(actions.updateJumpToSuccess(updatedNode));
      }
    } else {
      yield put(actions.updateJumpToSuccess());
    }
  } catch (e) {
    yield put(actions.updateJumpToError());
    yield put(actions.refreshNodes(skillId));
    yield put(
      addMessage({
        status: 'error',
        message: getError(e),
        transactionId: e.transactionId
      })
    );
  }
}

function* watchDuplicateNode() {
  yield takeEvery(actionType.DUPLICATE_NODE, duplicateNode);
}

function* watchUpdateJumpTo() {
  yield takeEvery(actionType.UPDATE_JUMP_TO, updateJumpTo);
}

export default function* rootSaga() {
  yield all([
    watchFetchNodes(),
    watchFetchNodesIfRequired(),
    watchRefreshNodes(),
    watchAddNode(),
    watchDeleteNode(),
    watchUpdateNode(),
    watchUpdateSlot(),
    watchUpdateHandlers(),
    watchUpdateSlotAndHandlers(),
    watchAddSlot(),
    watchAddResponse(),
    watchEnableMcr(),
    watchEnableSlots(),
    watchCalloutCallout(),
    watchMoveSubnode(),
    watchMoveNode(),
    watchDuplicateNode(),
    watchUpdateJumpTo()
  ]);
}
